import { gql } from '@plato/app-core/gql';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useQuery } from '@plato/app-core/hooks/use-query';
import { filterViewTypenames, SavedFilterViews } from '@plato/app-shared/components/saved-filter-views';
import { useDefaultMetric } from '@plato/app-shared/hooks/use-default-metric';
import type { CustomerListParams } from '@plato/app-shared/utils/customer-list';
import { omitEmpty } from '@plato/shared-util/object';
import { CustomerIcon } from '@plato/ui-legacy/assets/icons/customer-icon';
import { FilterLayout, FilterLayoutControls } from '@plato/ui-legacy/components/filters/filter-layout';
import { ListView as LegacyListView } from '@plato/ui-legacy/components/list-views/list-view';
import { TableFooterWithCount } from '@plato/ui-legacy/components/tables/table-footer-with-count';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { ListView, ListViewBody, ListViewHeader } from '@plato/ui/containers/list-view';
import { isEmpty, omit } from 'lodash-es';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerListParams } from '../hooks/use-customer-list-params.ts';
import { useCustomerListQuickFilters } from '../hooks/use-customer-list-quick-filters.ts';
import { CustomerListAppliedFilters } from './customer-list-applied-filters.tsx';
import { CustomerListFilters } from './customer-list-filters.tsx';
import { CustomerListTable } from './customer-list-table.tsx';

const CUSTOMER_LIST_QUERY = gql(/* GraphQL */ `
    query CustomerList(
        $filter: CustomerFilter
        $defaultMetricFilter: SalesMetricFilter!
        $includeGrossMargin: Boolean!
        $includeSummary: Boolean!
        $includeAppliedCustomers: Boolean!
        $includeAppliedSalesReps: Boolean!
        $inlcudeAppliedCompanies: Boolean!
        $appliedCustomerIds: [ID!]!
        $appliedSalesRepIds: [ID!]!
        $appliedCompanyIds: [ID!]!
        $savedViewKey: String!
    ) {
        ...CustomerListAppliedFilters_Query
        filterViews(viewKeys: [$savedViewKey]) {
            ...SavedFilterViews_FilterView
        }
        customers(filter: $filter, sortBy: { by: { field: customer_name }, direction: asc }) {
            ...CustomerListTable_CustomerConnection
            totalCount
        }
    }
`);

const savedViewKey = 'customers.list.v2';

export function CustomerListView(): ReactElement {
    const { t } = useTranslation('customer-list');
    const { canReadGrossMargin, enableResponsiveTables } = useAppConfig();

    const { params, setParams, defaultParams } = useCustomerListParams();
    const quickFilters = useCustomerListQuickFilters();
    const { sorting, ...filter } = params;

    const includeSummary = Object.values(omit(filter, 'searchString')).some((v) => !isEmpty(v));

    const defaultMetricFilter = useDefaultMetric();

    const [{ data, fetching: isLoading }] = useQuery({
        query: CUSTOMER_LIST_QUERY,
        variables: {
            filter: omitEmpty(filter),
            defaultMetricFilter,
            includeGrossMargin: canReadGrossMargin,
            includeSummary,
            includeAppliedCustomers: !!filter.ids?.length,
            includeAppliedSalesReps: !!filter.salesRepIds?.length,
            inlcudeAppliedCompanies: !!filter.companyIds?.length,
            appliedCustomerIds: filter.ids ?? [],
            appliedSalesRepIds: filter.salesRepIds ?? [],
            appliedCompanyIds: filter.companyIds ?? [],
            savedViewKey,
        },
        context: filterViewTypenames,
    });

    const filters = (
        <FilterLayout>
            <FilterLayoutControls>
                <CustomerListFilters />
                <SavedFilterViews<CustomerListParams>
                    views={data?.filterViews}
                    viewKey={savedViewKey}
                    appliedFilters={params}
                    defaultFilters={defaultParams}
                    onApplyFilters={setParams}
                    quickFilters={quickFilters}
                />
            </FilterLayoutControls>
            <CustomerListAppliedFilters data={data} />
        </FilterLayout>
    );

    if (!enableResponsiveTables) {
        return (
            <Page>
                <PageHeader
                    icon={CustomerIcon}
                    subTitle={t('page_sub_title__customers')}
                    title={t('page_title__customers')}
                />
                {filters}
                <LegacyListView>
                    <LegacyListView.BodyContainer>
                        <CustomerListTable summary={data?.customers} />
                        <TableFooterWithCount loading={isLoading} count={data?.customers.totalCount} />
                    </LegacyListView.BodyContainer>
                </LegacyListView>
            </Page>
        );
    }

    return (
        <ListView>
            <ListViewHeader>
                <PageHeader
                    className="mt-0"
                    icon={CustomerIcon}
                    subTitle={t('page_sub_title__customers')}
                    title={t('page_title__customers')}
                />
                {filters}
            </ListViewHeader>
            <ListViewBody>
                <CustomerListTable responsive summary={data?.customers} />
            </ListViewBody>
        </ListView>
    );
}
